/* eslint-disable react/no-unstable-nested-components */
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { Field } from "redux-form";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import { useMaterialUIController } from "context";

export const BOOKING_STATES = ["new", "update", "deleted"];

const BOOKING_STATUS_LABEL_KEYS = {
  new: "emailBookingStatusNew",
  update: "emailBookingStatusUpdate",
  deleted: "emailBookingStatusDeleted",
};

const BOOKING_TEXT_FIELDS = [
  { key: "subject", labelKey: "emailFieldSubjectSuffix" },
  { key: "emailTitle", labelKey: "emailFieldEmailTitle" },
  { key: "emailText", labelKey: "emailFieldEmailText" },
  { key: "pendingMessageText", labelKey: "emailFieldPendingMessageText" },
];

const SUPPORTED_LOCALES = ["es", "en"];

const BOOKING_DEFAULT_TEXTS = {
  new: {
    es: {
      subject: "Solicitud de reserva creada correctamente",
      emailTitle: "Solicitud de reserva para",
      emailText: "Hemos recibido tu solicitud de reserva:",
      pendingMessageText:
        "Estamos procesando tu solicitud, nos pondremos en contacto contigo lo antes posible con novedades.",
    },
    en: {
      subject: "Booking request created successfully",
      emailTitle: "Booking request for",
      emailText: "We have received your booking request:",
      pendingMessageText:
        "We are processing your request, we will get in contact as soon as possible with new updates.",
    },
  },
  update: {
    es: {
      subject: "Actualizacion del estado de reserva",
      emailTitle: "Actualizacion de reserva para",
      emailText: "Hemos actualizado el estado de tu solicitud de reserva:",
      pendingMessageText:
        "Estamos procesando tu solicitud, nos pondremos en contacto contigo lo antes posible con novedades.",
    },
    en: {
      subject: "Booking status update",
      emailTitle: "Booking update for",
      emailText: "We have updated the state of your booking request:",
      pendingMessageText:
        "We are processing your request, we will get in contact as soon as possible with new updates.",
    },
  },
  deleted: {
    es: {
      subject: "Reserva eliminada",
      emailTitle: "Reserva eliminada para",
      emailText: "Tu reserva ha sido eliminada. Estos son los detalles:",
    },
    en: {
      subject: "Booking deleted",
      emailTitle: "Booking deleted for",
      emailText: "Your booking has been deleted. These are the details:",
    },
  },
};

const getTextValue = (statusConfig, fieldKey, locale) => {
  const value = statusConfig?.texts?.[fieldKey]?.[locale];
  return typeof value === "string" ? value : "";
};

const getDefaultTextPlaceholder = (statusKey, fieldKey, locale) => {
  const stateKey = BOOKING_DEFAULT_TEXTS[statusKey] ? statusKey : "new";
  const safeLocale = locale === "en" ? "en" : "es";
  return BOOKING_DEFAULT_TEXTS[stateKey]?.[safeLocale]?.[fieldKey] || "";
};

const buildTextsByFieldAndLocale = (statusConfig = {}) =>
  Object.fromEntries(
    BOOKING_TEXT_FIELDS.map((field) => [
      field.key,
      Object.fromEntries(
        SUPPORTED_LOCALES.map((locale) => [locale, getTextValue(statusConfig, field.key, locale)])
      ),
    ])
  );

export const buildBookingStatesPayload = (statesSource) =>
  Object.fromEntries(
    BOOKING_STATES.map((statusKey) => {
      const statusConfig = statesSource?.[statusKey] || {};

      return [
        statusKey,
        {
          enabled: Boolean(statusConfig?.enabled),
          texts: buildTextsByFieldAndLocale(statusConfig),
        },
      ];
    })
  );

export const buildBookingExpandedStates = () =>
  Object.fromEntries(BOOKING_STATES.map((stateKey) => [stateKey, true]));

function BookingTextFields({ statusKey, t }) {
  const visibleTextFields =
    statusKey === "deleted"
      ? BOOKING_TEXT_FIELDS.filter((field) => field.key !== "pendingMessageText")
      : BOOKING_TEXT_FIELDS;

  return (
    <Grid container spacing={2} sx={{ marginLeft: 5 }}>
      {visibleTextFields.map((field) => (
        <Grid size={{ xs: 12 }} key={`${statusKey}-${field.key}`}>
          <MDTypography
            variant="button"
            fontWeight="medium"
            mb={1}
            display="block"
            sx={{ textTransform: "uppercase", fontSize: 14, marginTop: 3 }}
          >
            {t(field.labelKey)}
          </MDTypography>
          <Grid container spacing={2}>
            {SUPPORTED_LOCALES.map((locale) => (
              <Grid size={{ xs: 12, md: 6 }} key={locale}>
                <Field
                  name={`emailTemplates.booking.states.${statusKey}.texts.${field.key}.${locale}`}
                  component={FormField}
                  label={`${t(field.labelKey)} (${locale.toUpperCase()})`}
                  placeholder={getDefaultTextPlaceholder(statusKey, field.key, locale)}
                  multiline
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

BookingTextFields.propTypes = {
  statusKey: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

function BookingStatusSection({ statusKey, isExpanded, onToggleExpand, t }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <Grid size={{ xs: 12 }} key={statusKey}>
      <MDBox
        p={2}
        borderRadius="lg"
        style={{
          border: darkMode ? "1px solid #26304b" : "1px solid #e5e7eb",
          background: darkMode ? "#26304b" : "#f3f4f6",
        }}
      >
        <Field
          name={`emailTemplates.booking.states.${statusKey}.enabled`}
          component={({ input: statusInput }) => (
            <>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Checkbox
                  checked={!!statusInput.value}
                  onChange={(event) => statusInput.onChange(event.target.checked)}
                />
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: 16,
                    textTransform: "uppercase",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                  }}
                  onClick={() => onToggleExpand(statusKey)}
                >
                  {t(BOOKING_STATUS_LABEL_KEYS[statusKey])}
                </MDTypography>
              </MDBox>

              {statusInput.value && isExpanded && <BookingTextFields statusKey={statusKey} t={t} />}
            </>
          )}
        />
      </MDBox>
    </Grid>
  );
}

BookingStatusSection.propTypes = {
  statusKey: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function BookingEmailSection({
  expandedStates,
  isTemplateExpanded,
  onToggleStateExpanded,
  onToggleTemplateExpanded,
  t,
  templateInput,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={!!templateInput.value}
            onChange={(event) => templateInput.onChange(event.target.checked)}
          />
          <MDTypography
            variant="button"
            fontWeight="medium"
            textTransform="uppercase"
            sx={{
              cursor: "pointer",
              userSelect: "none",
              fontSize: 18,
              color: darkMode ? "#f8fafc" : "#0f172a",
            }}
            onClick={onToggleTemplateExpanded}
          >
            {t("emailBooking")}
          </MDTypography>
        </MDBox>
      </Grid>

      {templateInput.value &&
        isTemplateExpanded &&
        BOOKING_STATES.map((statusKey) => (
          <BookingStatusSection
            key={statusKey}
            statusKey={statusKey}
            isExpanded={!!expandedStates[statusKey]}
            onToggleExpand={onToggleStateExpanded}
            t={t}
          />
        ))}
    </>
  );
}

BookingEmailSection.propTypes = {
  expandedStates: PropTypes.object.isRequired,
  isTemplateExpanded: PropTypes.bool.isRequired,
  onToggleStateExpanded: PropTypes.func.isRequired,
  onToggleTemplateExpanded: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  templateInput: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
};

export default BookingEmailSection;
